import {
  BISHOP, CASTLE_BK, CASTLE_BQ, CASTLE_WK, CASTLE_WQ, EMPTY, KING, KNIGHT, NO_SQUARE, PAWN, QUEEN, ROOK,
  opposite, pieceCode, typeOf, type Color, type Position,
} from './types.js';
import { BOARD_SIZE, SQUARE_COUNT, fileOf, parseSquare, rowOf, squareAt, squareName } from './squares.js';
import { sanitizeCastling } from './castling.js';
import { isInCheck } from './attacks.js';

export const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export type FenResult =
  | { readonly ok: true; readonly position: Position }
  | { readonly ok: false; readonly error: string };

const LETTER_TO_TYPE: Readonly<Record<string, number>> = {
  p: PAWN, n: KNIGHT, b: BISHOP, r: ROOK, q: QUEEN, k: KING,
};
const TYPE_TO_LETTER = ['', 'p', 'n', 'b', 'r', 'q', 'k'];

const CASTLING_LETTERS: readonly (readonly [string, number])[] = [
  ['K', CASTLE_WK], ['Q', CASTLE_WQ], ['k', CASTLE_BK], ['q', CASTLE_BQ],
];

// Rows (FEN order) an en-passant target may sit on: rank 6 after a black
// double push (white to move), rank 3 after a white one.
const EP_ROW_WHITE_TO_MOVE = 2;
const EP_ROW_BLACK_TO_MOVE = 5;

// FEN without the two move counters (EPD style) is common in pasted input.
const MIN_FIELDS = 4;
const FULL_FIELDS = 6;

function parseBoard(placement: string): Int8Array | string {
  const rows = placement.split('/');
  if (rows.length !== BOARD_SIZE) return 'Board must have 8 ranks';
  const board = new Int8Array(SQUARE_COUNT);
  for (let row = 0; row < BOARD_SIZE; row++) {
    let file = 0;
    for (const ch of rows[row] ?? '') {
      if (ch >= '1' && ch <= '8') {
        file += Number(ch);
        continue;
      }
      const type = LETTER_TO_TYPE[ch.toLowerCase()];
      if (type === undefined) return `Unknown piece "${ch}"`;
      if (file >= BOARD_SIZE) return `Rank ${BOARD_SIZE - row} is too long`;
      board[row * BOARD_SIZE + file] = pieceCode(type, ch === ch.toUpperCase() ? 'w' : 'b');
      file++;
    }
    if (file !== BOARD_SIZE) return `Rank ${BOARD_SIZE - row} must have 8 squares`;
  }
  return board;
}

function validateMaterial(board: Int8Array): string | null {
  let whiteKings = 0;
  let blackKings = 0;
  for (let sq = 0; sq < SQUARE_COUNT; sq++) {
    const code = board[sq] ?? EMPTY;
    if (code === KING) whiteKings++;
    if (code === -KING) blackKings++;
    const row = rowOf(sq);
    if (typeOf(code) === PAWN && (row === 0 || row === BOARD_SIZE - 1)) return 'Pawns cannot stand on the first or last rank';
  }
  if (whiteKings !== 1 || blackKings !== 1) return 'Each side needs exactly one king';
  return null;
}

function parseCastling(field: string): number | null {
  if (field === '-') return 0;
  let rights = 0;
  for (const ch of field) {
    const entry = CASTLING_LETTERS.find(([letter]) => letter === ch);
    if (entry === undefined || (rights & entry[1]) !== 0) return null;
    rights |= entry[1];
  }
  return rights;
}

function parseCounter(field: string | undefined, fallback: number, min: number): number | null {
  if (field === undefined) return fallback;
  if (!/^\d+$/.test(field)) return null;
  const value = Number(field);
  return value >= min ? value : null;
}

export function parseFen(fen: string): FenResult {
  const fields = fen.trim().split(/\s+/);
  if (fields.length < MIN_FIELDS || fields.length > FULL_FIELDS) return { ok: false, error: 'FEN must have 4 to 6 fields' };
  const [placement = '', turnField, castlingField = '', epField = ''] = fields;

  const board = parseBoard(placement);
  if (typeof board === 'string') return { ok: false, error: board };
  const materialError = validateMaterial(board);
  if (materialError !== null) return { ok: false, error: materialError };

  if (turnField !== 'w' && turnField !== 'b') return { ok: false, error: 'Side to move must be "w" or "b"' };
  const turn: Color = turnField;

  const rawCastling = parseCastling(castlingField);
  if (rawCastling === null) return { ok: false, error: 'Invalid castling field' };
  const castling = sanitizeCastling(board, rawCastling);

  let epSquare = NO_SQUARE;
  if (epField !== '-') {
    epSquare = parseSquare(epField);
    const expectedRow = turn === 'w' ? EP_ROW_WHITE_TO_MOVE : EP_ROW_BLACK_TO_MOVE;
    if (epSquare === NO_SQUARE || rowOf(epSquare) !== expectedRow) return { ok: false, error: 'Invalid en-passant square' };
    // The pawn that just double-pushed stands one row past the target, toward its own side's far end.
    const pushedPawn = squareAt(fileOf(epSquare), rowOf(epSquare) + (turn === 'w' ? 1 : -1));
    if (board[epSquare] !== EMPTY || board[pushedPawn] !== pieceCode(PAWN, opposite(turn))) {
      return { ok: false, error: 'En-passant square has no pawn to capture' };
    }
  }

  // Otherwise the side to move could capture the king.
  if (isInCheck(board, opposite(turn))) return { ok: false, error: 'The side not to move is in check' };

  const halfmoveClock = parseCounter(fields[4], 0, 0);
  const fullmoveNumber = parseCounter(fields[5], 1, 1);
  if (halfmoveClock === null || fullmoveNumber === null) return { ok: false, error: 'Invalid move counters' };

  return { ok: true, position: { board, turn, castling, epSquare, halfmoveClock, fullmoveNumber } };
}

/** Parse a FEN known to be valid (constants, tests). Throws otherwise. */
export function fenToPosition(fen: string): Position {
  const result = parseFen(fen);
  if (!result.ok) throw new Error(`Invalid FEN "${fen}": ${result.error}`);
  return result.position;
}

function boardToFen(board: Int8Array): string {
  const rows: string[] = [];
  for (let row = 0; row < BOARD_SIZE; row++) {
    let text = '';
    let empty = 0;
    for (let file = 0; file < BOARD_SIZE; file++) {
      const code = board[row * BOARD_SIZE + file] ?? EMPTY;
      if (code === EMPTY) {
        empty++;
        continue;
      }
      if (empty > 0) text += String(empty);
      empty = 0;
      const letter = TYPE_TO_LETTER[typeOf(code)] ?? '';
      text += code > 0 ? letter.toUpperCase() : letter;
    }
    if (empty > 0) text += String(empty);
    rows.push(text);
  }
  return rows.join('/');
}

export function toFen(pos: Position): string {
  const castling = CASTLING_LETTERS.filter(([, bit]) => (pos.castling & bit) !== 0).map(([l]) => l).join('') || '-';
  const ep = pos.epSquare === NO_SQUARE ? '-' : squareName(pos.epSquare);
  return `${boardToFen(pos.board)} ${pos.turn} ${castling} ${ep} ${pos.halfmoveClock} ${pos.fullmoveNumber}`;
}
